import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import { Volume2 as AudioIcon } from 'lucide-mui';
import { FileType } from '../../../types';
import DefaultThumbnail from '../../../assets/recording/WhiteThumbnail.svg';
import { getFileUrl } from '../../../lib/routingUtils';

interface RecordingSuggestionsProps {
  recordings: FileType[];
  isLoading: boolean;
  fileThumbnails?: Array<{
    fileId: string;
    thumbnailUrl: string | null;
    isLoading: boolean;
  }>;
}

const RecordingSuggestions: React.FC<RecordingSuggestionsProps> = ({
  recordings,
  isLoading,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
        }}
      >
        <CircularProgress size={20} />
        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
          Searching recordings...
        </Typography>
      </Box>
    );
  }

  if (recordings.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 1,
        p: 1,
        backgroundColor: 'background.paper',
        borderRadius: '8px',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'text.secondary',
          mb: 1,
          display: 'block',
        }}
      >
        Found {recordings.length} recording{recordings.length !== 1 ? 's' : ''}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          pb: 0.5,
        }}
      >
        {recordings.map((recording) => {
          // Use the same logic as the library - file.thumbUrl with fallback to DefaultThumbnail
          const thumbnailUrl = recording.thumbUrl || DefaultThumbnail;

          // Check if it's an audio-only file
          const isAudioOnly =
            recording.streams?.length === 1 && recording.streams[0] === 'audio';

          const { _id: fileId, teamId, parentId } = recording;
          const fileUrl = getFileUrl({
            fileId,
            teamId,
            view: 'list',
            parentId,
            withBasePath: true,
          });

          return (
            <MuiLink
              key={recording._id}
              href={fileUrl}
              target="_blank"
              sx={{
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                minWidth: '140px',
                maxWidth: '180px',
                p: 1,
                borderRadius: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                border: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              {/* Thumbnail */}
              <Box
                sx={{
                  width: '100%',
                  height: 80,
                  borderRadius: '6px',
                  overflow: 'hidden',
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(42, 43, 44, 0.5)'
                      : 'action.hover',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  mb: 1,
                }}
              >
                {/* Show audio icon for audio-only files, otherwise show thumbnail */}
                {isAudioOnly ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                      color: 'text.secondary',
                    }}
                  >
                    <AudioIcon width={22} height={22} />
                  </Box>
                ) : (
                  <img
                    src={thumbnailUrl}
                    alt={`Thumbnail for ${recording.name}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter:
                        theme.palette.mode === 'dark' &&
                        thumbnailUrl === DefaultThumbnail
                          ? 'invert(1)'
                          : 'none',
                    }}
                    onError={(e) => {
                      // Fallback to default thumbnail on error (same as library)
                      if (e.currentTarget.src !== DefaultThumbnail) {
                        e.currentTarget.src = DefaultThumbnail;
                      }
                    }}
                  />
                )}
              </Box>

              {/* File name */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  color: 'text.primary',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: '1.2',
                  mb: 0.5,
                }}
              >
                {recording.name}
              </Typography>

              {/* Duration */}
              {recording.duration && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.6rem',
                  }}
                >
                  {Math.floor(recording.duration / 60)}:
                  {String(Math.floor(recording.duration % 60)).padStart(2, '0')}
                </Typography>
              )}
            </MuiLink>
          );
        })}
      </Box>
    </Box>
  );
};

export default RecordingSuggestions;
